// iter 275 round 36 — Pagina /dashboard/crediti
//
// Mostra: saldo mensile, saldo top-up, bucket, carry-cap, periodo, storico,
// pacchetti top-up acquistabili (in BETA: bottoni con tooltip "disponibili
// al lancio post-beta").

import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { deviceFingerprint } from "@/lib/deviceFingerprint";
import { isNative } from "@/lib/native";
import {
  CurrencyEur, Coins, Calendar, ArrowsClockwise, Lightning,
  Armchair, MapTrifold, Microphone, FilmSlate, ShoppingBag, ArrowLeft,
  Crown, ArrowRight, InstagramLogo, Gift, CheckCircle,
} from "@phosphor-icons/react";

const ACTION_LABELS = {
  photo_enhance: { label: "Migliora foto", Icon: Lightning },
  photo_stage: { label: "Arreda spazio vuoto", Icon: Armchair },
  photo_restyle: { label: "Rinnova arredo", Icon: Armchair },
  photo_empty_room: { label: "Svuota stanza", Icon: Armchair },
  floor_plan_furnish: { label: "Arreda pianta", Icon: MapTrifold },
  voice_transcribe: { label: "Sopralluogo vocale", Icon: Microphone },
  voice_property_fields: { label: "Estrazione dati da voce", Icon: Microphone },
  assistant_question: { label: "Assistente Pronto", Icon: Lightning },
  reel_before_after: { label: "Reel prima/dopo", Icon: FilmSlate },
  reel_with_voice: { label: "Reel con voce", Icon: FilmSlate },
  audio_check: { label: "Audio Check sopralluogo", Icon: Microphone },
  social_carousel: { label: "Carosello social", Icon: FilmSlate },
  social_post: { label: "Post singolo", Icon: FilmSlate },
  social_pack: { label: "Pack social completo", Icon: ShoppingBag },
  valuation: { label: "Valutazione immobile", Icon: CurrencyEur },
};

// Etichette del piano attivo · sync con /api/pricing e Pricing.jsx
const PLAN_DETAILS = {
  free: {
    name: "Free",
    price: "€0",
    period: "sempre gratuito",
    credits: "10 crediti al mese",
    free_help: "Con 10 crediti puoi migliorare qualche foto o generare contenuti brevi. Se finiscono prima, compri un pacchetto qui sotto (senza scadenza).",
    cta: "Passa a un piano superiore",
  },
  base: {
    name: "Start",
    price: "€29",
    period: "/ mese",
    credits: "150 crediti / mese · 5 immobili attivi",
    cta: "Gestisci abbonamento",
  },
  pro: {
    name: "Pro",
    price: "€49",
    period: "/ mese",
    credits: "320 crediti / mese · Assistente illimitato · immobili senza limite · niente watermark",
    cta: "Gestisci abbonamento",
  },
  beta: {
    name: "Gratis · illimitato",
    price: "€0",
    period: "in promo di lancio",
    credits: "Tutte le funzioni incluse durante la promo di lancio",
    cta: "Vedi piani al lancio",
  },
  pro_agency: {
    name: "Agenzia",
    price: "€99",
    period: "/ mese (in arrivo)",
    credits: "640 crediti / mese · fino a 5 agenti · brand condiviso · feed XML agenzia",
    cta: "Disponibile al lancio",
  },
};

export default function Crediti() {
  const [me, setMe] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [packages, setPackages] = useState([]);
  const [abuse, setAbuse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  // iter v29 r225 — Instagram bonus state
  const [igStatus, setIgStatus] = useState(null);
  const [claimingIg, setClaimingIg] = useState(false);
  const [igClicked, setIgClicked] = useState(false);
  // iter v29 r227 — username Instagram dell'utente per il claim
  const [igHandle, setIgHandle] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  // iter v30 r110 — Richiesta crediti extra durante promo gratuita
  const [requestingExtra, setRequestingExtra] = useState(false);

  const requestExtraCredits = async () => {
    setRequestingExtra(true);
    try {
      const motivation = window.prompt(
        "Raccontaci brevemente perché ti servono crediti extra (facoltativo):"
      );
      if (motivation === null) {
        setRequestingExtra(false);
        return; // utente ha annullato
      }
      const res = await api.post("/credits/request-extra", {
        motivation: motivation || "",
      });
      toast.success(res.data?.message || "Richiesta inviata · ti risponderemo entro 24h");
    } catch (e) {
      toast.error(apiError(e, "Errore nell'invio della richiesta"));
    } finally {
      setRequestingExtra(false);
    }
  };

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const [meRes, ledRes, pkgRes, abuseRes, igRes] = await Promise.all([
          api.get("/credits/me"),
          api.get("/credits/ledger", { params: { limit: 30 } }),
          api.get("/credits/topup-packages"),
          api.get("/abuse/me").catch(() => ({ data: null })),
          api.get("/credits/instagram-follow-status").catch(() => ({ data: null })),
        ]);
        if (!on) return;
        setMe(meRes.data);
        setLedger(ledRes.data?.items || []);
        setPackages(pkgRes.data?.packages || []);
        setAbuse(abuseRes.data);
        setIgStatus(igRes.data);
      } catch (e) {
        toast.error(apiError(e, "Errore caricamento crediti"));
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => { on = false; };
  }, []);

  // iter v29 r225 — Stripe checkout return polling.
  // Quando l'utente torna dal Stripe Checkout con ?session_id=..., pollo
  // il backend ogni 2s fino a 6 tentativi per confermare l'accredito.
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const cancelled = searchParams.get("cancelled");
    if (cancelled) {
      toast.info("Pagamento annullato. Nessun addebito.");
      // Clear params from URL
      searchParams.delete("cancelled");
      setSearchParams(searchParams, { replace: true });
      return;
    }
    if (!sessionId) return;
    // Clear session_id from URL immediately to prevent re-polling on refresh
    searchParams.delete("session_id");
    setSearchParams(searchParams, { replace: true });

    let attempts = 0;
    const maxAttempts = 6;
    const pollInterval = 2000;
    const toastId = toast.loading("Verifica pagamento in corso…");

    const poll = async () => {
      attempts++;
      try {
        const res = await api.get(`/credits/topup/status/${sessionId}`);
        const d = res.data || {};
        if (d.payment_status === "paid" && d.credited) {
          toast.success(`+${d.credits} crediti accreditati. Grazie!`, { id: toastId });
          // Refresh balance
          const meRes = await api.get("/credits/me");
          setMe(meRes.data);
          const ledRes = await api.get("/credits/ledger", { params: { limit: 30 } });
          setLedger(ledRes.data?.items || []);
          return;
        }
        if (d.status === "expired") {
          toast.error("Sessione scaduta. Riprova.", { id: toastId });
          return;
        }
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          toast.warning("Verifica in sospeso. Controlla la mail per la conferma.", { id: toastId });
        }
      } catch {
        if (attempts < maxAttempts) setTimeout(poll, pollInterval);
        else toast.error("Errore verifica pagamento", { id: toastId });
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buyTopup = async (sku) => {
    setBuying(sku);
    try {
      const res = await api.post("/credits/topup/checkout", {
        sku,
        origin_url: window.location.origin,
      });
      if (res.data?.disabled) {
        toast.info(res.data.message || "Crediti aggiuntivi disponibili a breve");
        return;
      }
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Errore: nessun URL di checkout");
      }
    } catch (e) {
      toast.error(apiError(e, "Errore acquisto pacchetto"));
    } finally {
      setBuying(null);
    }
  };

  // iter v29 r225 / r227 — Instagram bonus claim con username + check anti-frode.
  // 1. Click "Apri @prontoimmo" → segue
  // 2. Inserisce il proprio @username → +5 crediti se username esiste su IG
  //    e non già reclamato
  const openInstagram = () => {
    window.open(igStatus?.instagram_url || "https://www.instagram.com/prontoimmo.it/", "_blank", "noopener,noreferrer");
    setIgClicked(true);
  };

  const claimInstagramBonus = async () => {
    const handle = (igHandle || "").trim().replace(/^@/, "");
    if (!handle) {
      toast.error("Inserisci il tuo username Instagram.");
      return;
    }
    if (!/^[a-zA-Z0-9._]{1,30}$/.test(handle)) {
      toast.error("Username non valido. Solo lettere, numeri, punto e underscore.");
      return;
    }
    setClaimingIg(true);
    try {
      // Device fingerprint stabile (canvas + screen + UA) per anti-frode
      const fp = await deviceFingerprint();
      const res = await api.post("/credits/instagram-follow-claim", {
        instagram_username: handle,
        device_fp: fp,
      });
      toast.success(`+${res.data?.credits_granted || 5} crediti accreditati. Grazie!`);
      const [meRes, igRes] = await Promise.all([
        api.get("/credits/me"),
        api.get("/credits/instagram-follow-status"),
      ]);
      setMe(meRes.data);
      setIgStatus(igRes.data);
    } catch (e) {
      toast.error(apiError(e, "Verifica fallita"));
    } finally {
      setClaimingIg(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="px-4 py-12 text-center text-[#6B6A65]">Caricamento crediti…</div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="px-4 sm:px-6 py-6 max-w-4xl mx-auto" data-testid="crediti-page">
        <a href="/dashboard" className="text-[12px] text-[#6B6A65] hover:text-[#1C1C1A] inline-flex items-center gap-1 mb-3">
          <ArrowLeft size={11} weight="bold" /> Dashboard
        </a>
        <h1 className="font-editorial text-2xl sm:text-3xl text-[#1C1C1A]">Crediti e piano</h1>
        <p className="hidden sm:block text-[13px] text-[#6B6A65] mt-1">
          Ogni elaborazione automatica (migliora foto, arreda, restyle, sopralluogo, reel) consuma crediti dal tuo bucket mensile. Brochure, post e didascalie sono gratuite.
        </p>
        <p className="sm:hidden text-[12.5px] text-[#6B6A65] mt-1">
          Foto, arredi, sopralluogo e reel consumano crediti. Post e brochure sono gratuite.
        </p>

        {/* iter v27 — Piano attivo: prezzo, crediti inclusi, CTA cambio piano */}
        {me?.plan && PLAN_DETAILS[me.plan] && (
          <div
            className="mt-5 p-4 sm:p-5 rounded-lg border border-[#E5E7E0] bg-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            data-testid="crediti-current-plan"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F1F4ED] flex items-center justify-center flex-shrink-0">
                <Crown size={18} weight="duotone" className="text-[#7C8B6E]" />
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#9A968B] font-semibold">Piano attivo</div>
                <div className="flex items-baseline gap-2 mt-0.5">
                  <span className="font-editorial text-xl text-[#1C1C1A]">{PLAN_DETAILS[me.plan].name}</span>
                  <span className="text-[15px] font-semibold text-[#1C1C1A]">{PLAN_DETAILS[me.plan].price}</span>
                  {PLAN_DETAILS[me.plan].period && (
                    <span className="text-[12px] text-[#6B6A65]">{PLAN_DETAILS[me.plan].period}</span>
                  )}
                </div>
                <div className="text-[12.5px] text-[#6B6A65] mt-0.5">{PLAN_DETAILS[me.plan].credits}</div>
              </div>
            </div>
            {isNative ? (
              // iter v31 r2 — Su iOS la gestione piano DEVE passare dal paywall
              // IAP interno (Apple 3.1.1 + anti-steering). Niente customer portal
              // Stripe esterno.
              <Link
                to="/dashboard/upgrade"
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded text-[12.5px] font-semibold border border-[#7C8B6E] text-[#7C8B6E] hover:bg-[#7C8B6E] hover:text-white transition-colors whitespace-nowrap"
                data-testid="crediti-manage-plan-ios"
              >
                Cambia piano <ArrowRight size={12} weight="bold" />
              </Link>
            ) : ["base", "pro"].includes(me.plan) && !me?.beta_mode ? (
              <button
                type="button"
                onClick={async () => {
                  setOpeningPortal(true);
                  try {
                    const { data } = await api.post("/payments/customer-portal", {
                      return_url: window.location.origin + "/dashboard/crediti",
                    });
                    if (data?.url) window.location.href = data.url;
                  } catch (e) {
                    toast.error(apiError(e, "Errore apertura portale"));
                  } finally {
                    setOpeningPortal(false);
                  }
                }}
                disabled={openingPortal}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded text-[12.5px] font-semibold border border-[#7C8B6E] text-[#7C8B6E] hover:bg-[#7C8B6E] hover:text-white transition-colors whitespace-nowrap disabled:opacity-50"
                data-testid="crediti-open-portal"
              >
                {openingPortal ? "Apertura..." : "Gestisci abbonamento"} <ArrowRight size={12} weight="bold" />
              </button>
            ) : (
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded text-[12.5px] font-semibold border border-[#7C8B6E] text-[#7C8B6E] hover:bg-[#7C8B6E] hover:text-white transition-colors whitespace-nowrap"
                data-testid="crediti-manage-plan"
              >
                {PLAN_DETAILS[me.plan].cta} <ArrowRight size={12} weight="bold" />
              </Link>
            )}
          </div>
        )}

        {me?.beta_mode && (
          <div className="mt-4 p-3 rounded-lg bg-[#FFF8E6] border border-[#E8DCAE] text-[12.5px] text-[#7A6212]" data-testid="crediti-beta-banner">
            <strong>Gratis · Tutto incluso</strong>
            <span className="hidden sm:inline"> tutte le funzioni di Pronto sono incluse senza vincoli. Il contatore qui sotto è informativo.</span>
            <span className="sm:hidden"> . Contatore informativo.</span>
          </div>
        )}

        {/* iter 275 round 50 · Health anti-abuso */}
        <AbuseStatusBanner abuse={abuse} />

        {/* iter v30 r099 — Chiarezza piano gratuito: spieghiamo cosa si può
            fare con 10 crediti/mese e dove comprarne altri (Brief utente:
            «in questa versione gratuita quanti crediti si hanno al giorno?
            si possono comprare i crediti singoli?»). */}
        {me?.plan === "free" && PLAN_DETAILS.free.free_help && (
          <div className="mt-3 p-3 rounded-lg bg-[#F1F4ED] border border-[#D9E0CF] text-[12.5px] text-[#3F4A33]" data-testid="crediti-free-help">
            <strong>Come funziona il piano Free</strong>
            <p className="mt-1 leading-relaxed">{PLAN_DETAILS.free.free_help}</p>
            <p className="mt-1.5 text-[#5E6E51]">
              I crediti mensili del piano Free si rinnovano ogni mese e non si accumulano. I 60 crediti gratis all&apos;iscrizione e i crediti acquistati a parte restano disponibili fino a esaurimento.
            </p>
          </div>
        )}

        {/* Saldo */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <BalanceCard
            label="Inclusi nel piano"
            value={me.balance_monthly}
            max={me.bucket_size}
            Icon={Coins}
            sub={`Si rinnovano il ${formatDate(me.period_end)}`}
            testid="crediti-monthly"
          />
          <BalanceCard
            label="Crediti aggiuntivi"
            value={me.balance_topup}
            Icon={ShoppingBag}
            sub="Non scadono mai"
            testid="crediti-topup"
          />
          <BalanceCard
            label="Totale disponibile"
            value={me.balance_total}
            Icon={CurrencyEur}
            sub={`Si accumulano fino a ${me.carry_cap}`}
            highlight
            testid="crediti-total"
          />
        </div>

        {/* iter v29 r225 — Bonus Instagram follow (+5 crediti una tantum) */}
        {igStatus && (
          <section className="mt-6" data-testid="crediti-instagram-bonus">
            {igStatus.claimed ? (
              <div
                className="p-3.5 rounded-lg bg-[#F1F4ED] border border-[#7C8B6E]/30 flex items-center gap-3"
                data-testid="ig-bonus-claimed"
              >
                <div className="w-9 h-9 rounded-full bg-[#7C8B6E] flex items-center justify-center shrink-0">
                  <CheckCircle size={18} weight="fill" className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[#1C1C1A]">
                    Bonus Instagram già ricevuto
                  </div>
                  <div className="text-[11.5px] text-[#6B6A65]">
                    Grazie per seguirci. I {igStatus.bonus_amount || 5} crediti sono nel tuo saldo.
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="p-4 rounded-lg bg-white border border-[#EAE7DF]"
                data-testid="ig-bonus-card"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-md bg-[#F1F4ED] flex items-center justify-center shrink-0">
                    <InstagramLogo size={18} weight="duotone" className="text-[#7C8B6E]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-[14px] font-semibold text-[#1C1C1A]">
                        Bonus · +{igStatus.bonus_amount || 5} crediti per chi ci segue
                      </h3>
                      <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[#F1F4ED] text-[#5E6E51]">
                        Una tantum
                      </span>
                    </div>
                    <p className="text-[12px] text-[#6B6A65] mt-1 leading-snug">
                      Segui <strong className="text-[#1C1C1A]">{igStatus.instagram_handle || "@prontoimmo"}</strong> e poi
                      inserisci qui sotto il tuo username Instagram. Verifichiamo che il profilo esista e
                      accreditiamo {igStatus.bonus_amount || 5} crediti.
                    </p>

                    {/* Step 1 — apri Instagram */}
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={openInstagram}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[12px] font-semibold text-[#1C1C1A] bg-[#F1F4ED] hover:bg-[#E5EBDE] transition-colors border border-[#D5DCC9]"
                        data-testid="ig-bonus-open"
                      >
                        <InstagramLogo size={13} weight="bold" />
                        1 · Apri {igStatus.instagram_handle || "@prontoimmo"} e segui
                      </button>
                    </div>

                    {/* Step 2 — username + claim */}
                    <div className={`mt-2.5 flex flex-wrap items-stretch gap-2 ${!igClicked ? "opacity-50 pointer-events-none" : ""}`}>
                      <div className="flex items-center bg-white border border-[#D5DCC9] rounded overflow-hidden">
                        <span className="px-2.5 text-[13px] text-[#9A968B] border-r border-[#EAE7DF] bg-[#FAFAF7]">@</span>
                        <input
                          type="text"
                          value={igHandle}
                          onChange={(e) => setIgHandle(e.target.value.replace(/^@/, "").toLowerCase())}
                          placeholder="tuo.username"
                          maxLength={30}
                          autoCapitalize="off"
                          autoCorrect="off"
                          spellCheck="false"
                          className="px-2.5 py-1.5 text-[13px] text-[#1C1C1A] outline-none w-44"
                          data-testid="ig-bonus-username-input"
                          disabled={!igClicked || claimingIg}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={claimInstagramBonus}
                        disabled={!igClicked || claimingIg || !igHandle.trim()}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[12px] font-semibold bg-[#1C1C1A] text-white hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                        data-testid="ig-bonus-claim"
                      >
                        <Gift size={13} weight="bold" />
                        {claimingIg ? "Verifica in corso…" : `2 · Riscuoti +${igStatus.bonus_amount || 5}`}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>
        )}

        {/* iter v31 r43 — PACCHETTI CREDITI ATTIVATI durante la promo.
            Il piano FREE (60 cr/mese) resta gratis,
            ma chi vuole crediti extra può comprare un top-up subito.
            Gli ABBONAMENTI restano bloccati lato backend (3 mesi gratis
            su Stripe/Apple gestiti dalla piattaforma). */}
        <section className="mt-8" data-testid="crediti-promo-banner">
          <div className="rounded-lg border border-[#E5E7E0] bg-white p-5">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#7C8B6E] bg-[#F1F4ED] border border-[#D8DECE] px-2 py-0.5 rounded-full">
                Promo
              </span>
              <span className="text-[12px] text-[#6B6A65]">Periodo di lancio</span>
            </div>
            <h2 className="mt-2 text-[18px] font-semibold text-[#1C1C1A]">
              Pronto è gratis · {me.bucket_size || 60} crediti al mese
            </h2>
            <p className="mt-1.5 text-[13px] text-[#6B6A65] leading-snug">
              Tutte le funzioni sono incluse: voce, foto, agenda, assistente, WhatsApp.
              Se ti servono più crediti, qui sotto trovi i pacchetti aggiuntivi (non scadono).
            </p>

            {/* iter v30 r112 — Contatore consumo + data rinnovo + esempi concreti */}
            {(() => {
              const bucket = Number(me.bucket_size || 60);
              const balance = Number(me.balance_monthly ?? bucket);
              const used = Math.max(0, bucket - balance);
              const pct = bucket > 0 ? Math.min(100, Math.round((used / bucket) * 100)) : 0;
              // Prossimo rinnovo = primo del mese prossimo
              const now = new Date();
              const nextRenewal = new Date(now.getFullYear(), now.getMonth() + 1, 1);
              const renewalLabel = nextRenewal.toLocaleDateString("it-IT", {
                day: "numeric", month: "long",
              });
              const costs = me.action_costs || {};
              const photoEnh = Math.floor(balance / (costs.photo_enhance || 1));
              const stage = Math.floor(balance / (costs.photo_stage || 5));
              const reels = Math.floor(balance / (costs.reel_with_voice || 5));
              return (
                <div className="mt-4 rounded-lg bg-[#FAF9F5] border border-[#EAE7DF] p-3.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="text-[12.5px] text-[#1C1C1A]">
                      <span className="font-semibold">{balance} cr</span>
                      <span className="text-[#6B6A65]"> di {bucket} · rinnovo {renewalLabel}</span>
                    </div>
                    <div className="text-[10.5px] text-[#9A968B] uppercase tracking-[0.14em]">
                      {pct}% usato
                    </div>
                  </div>
                  <div className="mt-2 h-1.5 w-full rounded-full bg-[#EAE7DF] overflow-hidden">
                    <div
                      className="h-full bg-[#7C8B6E] transition-all"
                      style={{ width: `${pct}%` }}
                      data-testid="crediti-progress-bar"
                    />
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                    <div className="rounded bg-white border border-[#EAE7DF] py-2">
                      <div className="text-[18px] font-semibold text-[#1C1C1A] leading-none">{stage}</div>
                      <div className="text-[10px] text-[#6B6A65] uppercase tracking-wide mt-1">staging</div>
                    </div>
                    <div className="rounded bg-white border border-[#EAE7DF] py-2">
                      <div className="text-[18px] font-semibold text-[#1C1C1A] leading-none">{photoEnh}</div>
                      <div className="text-[10px] text-[#6B6A65] uppercase tracking-wide mt-1">foto migliori</div>
                    </div>
                    <div className="rounded bg-white border border-[#EAE7DF] py-2">
                      <div className="text-[18px] font-semibold text-[#1C1C1A] leading-none">{reels}</div>
                      <div className="text-[10px] text-[#6B6A65] uppercase tracking-wide mt-1">reel</div>
                    </div>
                  </div>
                  <div className="mt-2 text-[10.5px] text-[#9A968B] text-center">
                    coi crediti rimasti puoi fare ad esempio:
                  </div>

                  {/* iter v31 r43 — IDEA BONUS: micro-banner di alert quando
                      il saldo è al di sotto del 30% del bucket. Nudge gentile
                      verso il pacchetto più piccolo (€5 / 20 cr). */}
                  {pct >= 70 && (
                    <div
                      className="mt-3 rounded-md bg-[#FFF8E6] border border-[#E8DCAE] p-2.5 flex items-center gap-2"
                      data-testid="crediti-low-balance-nudge"
                    >
                      <Lightning size={13} weight="fill" className="text-[#B57C5E] shrink-0" />
                      <span className="flex-1 text-[11.5px] text-[#7A6212] leading-snug">
                        Stai finendo i crediti del mese · 20 crediti extra a €5
                      </span>
                      <button
                        type="button"
                        onClick={() => buyTopup("topup_20")}
                        disabled={buying === "topup_20"}
                        className="shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#1C1C1A] hover:bg-black text-white text-[10.5px] font-semibold transition-colors disabled:opacity-60"
                        data-testid="crediti-low-balance-cta"
                      >
                        {buying === "topup_20" ? "…" : "Compra €5"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="mt-4 text-[11px] text-[#9A968B] flex items-center gap-1.5">
              <CheckCircle size={11} weight="fill" className="text-[#7C8B6E]" />
              Abbonamenti Start / Pro disponibili con l&apos;attivazione della fatturazione. Annuale: 3 mesi in regalo.
            </div>
          </div>
        </section>

        {/* PACCHETTI CREDITI ACQUISTABILI — Stripe Checkout */}
        <section className="mt-8" data-testid="crediti-topup-grid">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-[15px] font-semibold text-[#1C1C1A]">
              Crediti extra
            </h2>
            <span className="text-[11px] text-[#9A968B]">
              Si sommano al saldo · non scadono mai
            </span>
          </div>
          {packages.length === 0 ? (
            <div className="rounded-lg bg-[#FAF9F5] border border-[#EAE7DF] p-4 text-[12.5px] text-[#9A968B] text-center">
              Caricamento pacchetti…
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
              {packages.map((pkg) => {
                const isLoading = buying === pkg.sku;
                const isRecommended = pkg.sku === "topup_165"; // best value
                return (
                  <button
                    key={pkg.sku}
                    type="button"
                    onClick={() => buyTopup(pkg.sku)}
                    disabled={isLoading}
                    className={`relative text-left rounded-lg border p-3 transition-all active:scale-[0.98] disabled:opacity-60 ${
                      isRecommended
                        ? "bg-[#1C1C1A] border-[#1C1C1A] text-white hover:bg-black"
                        : "bg-white border-[#EAE7DF] text-[#1C1C1A] hover:border-[#7C8B6E] hover:shadow-[0_4px_12px_-4px_rgba(28,28,26,0.12)]"
                    }`}
                    data-testid={`crediti-topup-${pkg.sku}`}
                  >
                    {isRecommended && (
                      <span className="absolute -top-1.5 right-2 text-[8.5px] font-bold uppercase tracking-[0.18em] bg-[#B57C5E] text-white px-1.5 py-0.5 rounded-sm">
                        Top
                      </span>
                    )}
                    <div className={`text-[9.5px] uppercase tracking-[0.2em] font-bold ${isRecommended ? "text-white/70" : "text-[#9A968B]"}`}>
                      {pkg.label}
                    </div>
                    <div className={`mt-1 text-[20px] font-semibold leading-none ${isRecommended ? "text-white" : "text-[#1C1C1A]"}`}>
                      {pkg.credits} cr
                    </div>
                    <div className={`mt-1 text-[16px] font-bold leading-none ${isRecommended ? "text-white" : "text-[#7C8B6E]"}`}>
                      €{Number(pkg.price_eur).toLocaleString("it-IT", { minimumFractionDigits: 0 })}
                    </div>
                    <div className={`mt-2 text-[10px] leading-snug ${isRecommended ? "text-white/70" : "text-[#6B6A65]"}`}>
                      {pkg.use_case}
                    </div>
                    {isLoading && (
                      <div className={`mt-2 text-[10px] ${isRecommended ? "text-white" : "text-[#7C8B6E]"} font-semibold`}>
                        Apertura checkout…
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          <div className="mt-3 text-[10.5px] text-[#9A968B] flex items-center gap-1.5">
            <CheckCircle size={11} weight="fill" className="text-[#7C8B6E]" />
            Pagamento sicuro tramite Stripe · Carta · Apple Pay · Google Pay
          </div>
        </section>

        {/* Costo per azione */}
        <section className="mt-8" data-testid="crediti-action-costs">
          <h2 className="text-[15px] font-semibold text-[#1C1C1A]">Quanto costa ogni azione</h2>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.entries(me.action_costs || {})
              .filter(([k]) => ACTION_LABELS[k])
              .map(([k, cost]) => {
                const { label, Icon } = ACTION_LABELS[k];
                return (
                  <div key={k} className="flex items-center gap-3 p-2.5 rounded bg-[#FAF9F5]">
                    <Icon size={18} weight="duotone" className="text-[#7C8B6E]" />
                    <span className="flex-1 text-[12.5px] text-[#1C1C1A]">{label}</span>
                    <span className="text-[11px] font-bold text-[#1C1C1A]">{cost} cr</span>
                  </div>
                );
              })}
          </div>
        </section>

        {/* Storico — iter v28 r96: collassabile + preview di 5 (era sempre
            aperta con tutti i 30 movimenti, troppo lunga). L'utente apre
            il dettaglio solo quando serve. */}
        <section className="mt-8" data-testid="crediti-ledger">
          <details className="bg-white border border-[#EAE7DF] rounded-lg">
            <summary className="cursor-pointer list-none p-3 flex items-center justify-between gap-2 hover:bg-[#FAFAF7]">
              <div className="flex items-center gap-2 text-[14px] font-semibold text-[#1C1C1A]">
                <ArrowsClockwise size={14} weight="bold" />
                Storico movimenti
                {ledger.length > 0 && (
                  <span className="text-[11px] text-[#9A968B] font-normal">· {ledger.length}</span>
                )}
              </div>
              <span className="text-[11px] text-[#6B6A65]">apri / chiudi</span>
            </summary>
            <div className="border-t border-[#F0EFE9]">
              {ledger.length === 0 ? (
                <div className="p-4 text-[12.5px] text-[#9A968B]">Nessun movimento ancora.</div>
              ) : (
                <CreditLedgerList rows={ledger} />
              )}
            </div>
          </details>
        </section>
      </div>
    </AppShell>
  );
}

function CreditLedgerList({ rows }) {
  // iter v28 r96 — Preview 5 movimenti recenti; "Mostra tutti" per espandere.
  const PREVIEW_COUNT = 5;
  const [showAll, setShowAll] = React.useState(false);
  const visible = showAll ? rows : rows.slice(0, PREVIEW_COUNT);
  const hidden = rows.length - visible.length;
  return (
    <>
      <div className="divide-y divide-[#F0EFE9]">
        {visible.map((row, i) => (
          <div key={i} className="flex items-center gap-3 p-2.5 text-[12px]" data-testid={`crediti-ledger-row-${i}`}>
            <Calendar size={12} weight="duotone" className="text-[#9A968B]" />
            <span className="text-[#6B6A65] whitespace-nowrap">{formatDate(row.created_at)}</span>
            <span className="flex-1 text-[#1C1C1A] truncate">{row.reason}</span>
            <span className={`font-mono font-semibold ${row.delta > 0 ? "text-[#7C8B6E]" : row.delta < 0 ? "text-red-600" : "text-[#9A968B]"}`}>
              {row.delta > 0 ? "+" : ""}{row.delta}
            </span>
          </div>
        ))}
      </div>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="w-full p-2.5 text-[12px] text-[#7C8B6E] font-semibold hover:bg-[#F4F6F0] border-t border-[#F0EFE9]"
          data-testid="crediti-ledger-show-all"
        >
          Mostra tutti i {rows.length} movimenti
        </button>
      )}
      {showAll && rows.length > PREVIEW_COUNT && (
        <button
          type="button"
          onClick={() => setShowAll(false)}
          className="w-full p-2.5 text-[12px] text-[#6B6A65] hover:text-[#1C1C1A] hover:bg-[#FAFAF7] border-t border-[#F0EFE9]"
          data-testid="crediti-ledger-show-less"
        >
          Mostra solo gli ultimi 5
        </button>
      )}
    </>
  );
}

function BalanceCard({ label, value, max, Icon, sub, highlight, testid }) {
  const pct = max ? Math.min(100, Math.round((value / max) * 100)) : null;
  return (
    <div
      className={`p-4 rounded-lg border ${highlight ? "bg-[#1C1C1A] text-white border-[#1C1C1A]" : "bg-white border-[#EAE7DF]"}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] opacity-70">
        <Icon size={12} weight="bold" /> {label}
      </div>
      <div className="font-editorial text-3xl mt-1.5">{value}</div>
      {pct !== null && (
        <div className={`mt-2 h-1.5 rounded-full overflow-hidden ${highlight ? "bg-white/15" : "bg-[#F1F4ED]"}`}>
          <div className={`h-full ${highlight ? "bg-white" : "bg-[#7C8B6E]"}`} style={{ width: `${pct}%` }} />
        </div>
      )}
      {sub && <div className="mt-1.5 text-[11px] opacity-80">{sub}</div>}
    </div>
  );
}

function AbuseStatusBanner({ abuse }) {
  if (!abuse) return null;
  if (abuse.whitelisted) return null; // founder/QA: niente banner

  const used = abuse.used_this_month || 0;
  const cap = abuse.cap_this_month || 0;
  const remaining = Math.max(0, cap - used);
  const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const isBanned = !!abuse.ban_active;
  const isFlagged = !!abuse.flagged;
  const isHigh = pct >= 80;

  const tone = isBanned
    ? { bg: "#FFEAE6", border: "#F4C2B7", text: "#8A2A14" }
    : isHigh
      ? { bg: "#FFF4E5", border: "#F2D9B0", text: "#7A4E0E" }
      : { bg: "#F1F4ED", border: "#D9E0CF", text: "#3F4A33" };

  const banMins = Math.max(1, Math.round((abuse.ban_seconds_remaining || 0) / 60));

  return (
    <div
      className="mt-3 p-3 rounded-lg border text-[12.5px]"
      style={{ background: tone.bg, borderColor: tone.border, color: tone.text }}
      data-testid="abuse-status-banner"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className={`inline-block w-2 h-2 rounded-full ${isBanned ? "bg-red-500" : isHigh ? "bg-amber-500" : "bg-green-500"}`} />
          <strong>Stato utilizzo</strong>
          {isFlagged && (
            <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-white/60 border border-current/30">
              account in revisione
            </span>
          )}
        </div>
        <div className="text-[11.5px] opacity-90" data-testid="abuse-counter">
          {used} / {cap} generazioni questo mese · {remaining} rimanenti
        </div>
      </div>

      <div className="mt-2 h-1.5 rounded-full overflow-hidden bg-white/60">
        <div
          className="h-full"
          style={{
            width: `${pct}%`,
            background: isBanned ? "#C0392B" : isHigh ? "#D98E1E" : "#7C8B6E",
          }}
        />
      </div>

      {isBanned ? (
        <div className="mt-2 text-[11.5px]">
          Troppe generazioni in pochi minuti. Riprova fra <strong>{banMins} minuti</strong>. Serve a tenere i costi sotto controllo.
        </div>
      ) : isHigh ? (
        <div className="mt-2 text-[11.5px]">
          Stai per raggiungere il tetto mensile del piano {abuse.plan}. Compra crediti aggiuntivi qui sotto per continuare senza interruzioni.
        </div>
      ) : null}
    </div>
  );
}


function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  } catch {
    return "—";
  }
}
